import type { ReactElement } from "react";

import { BookAccountant } from "../components/book-accountant.js";
import { BookExposure } from "../components/book-exposure.js";
import { BookFunds } from "../components/book-funds.js";
import { Figure, KpiStrip } from "../components/tile.js";
import type { BookExposureView, FundingPlanView } from "../api/client.js";

/**
 * Bookmaker accounts: where the money is and which accounts can still take a
 * bet.
 *
 * Its own page rather than a panel on the ledger, because every other page
 * hangs off the sport selector and accounts do not. A balance is not MLB's
 * balance, and a book that limits stops taking bets for every sport at once -
 * so showing this under a sport filter invites the question of whether the
 * turnover is that sport's or all of it.
 *
 * It also keeps out of Analytics' way. Analytics already groups performance by
 * bookmaker and answers "is this book giving me value". This answers a
 * different question - can it take the bet, and should it - which is about
 * funds, limiting risk and how replaceable the account is.
 */
const money = (cents: number): string =>
  `${cents < 0 ? "-" : ""}$${(Math.abs(cents) / 100).toFixed(2)}`;

export function AccountsPage({
  books = [],
  funding,
  onStatusChange,
  onFunds,
}: {
  readonly books?: readonly BookExposureView[];
  readonly funding?: FundingPlanView;
  readonly onStatusChange?: (
    bookmaker: string,
    status: "ACTIVE" | "LIMITED" | "CLOSED",
  ) => void;
  readonly onFunds?: (input: {
    bookmaker: string;
    kind: "DEPOSIT" | "WITHDRAWAL" | "BONUS" | "RECONCILE" | "ADJUSTMENT";
    amountCents: number;
    note: string | null;
  }) => void;
}): ReactElement {
  const active = books.filter((book) => book.status === "ACTIVE");
  const available = books.reduce((sum, book) => sum + book.availableCents, 0);
  const pending = books.reduce((sum, book) => sum + book.pendingCents, 0);
  const low = active.filter((book) => book.lowBalance).length;
  // An account that prices props is the hard one to replace, so how much money
  // sits behind that capability is worth its own figure.
  const propCapable = active.filter((book) => book.scarcity >= 0.05);
  const propFunds = propCapable.reduce(
    (sum, book) => sum + book.availableCents,
    0,
  );

  return (
    <div className="board-grid">
      <KpiStrip columns={4}>
        <Figure
          label="Available"
          value={money(available)}
          sub={`${active.length} active account${active.length === 1 ? "" : "s"}`}
        />
        <Figure
          label="In play"
          value={money(pending)}
          sub="stake on unsettled bets"
        />
        <Figure
          label="Behind props"
          value={money(propFunds)}
          sub={
            propCapable.length === 1
              ? "1 book prices props"
              : `${String(propCapable.length)} books price props`
          }
        />
        <Figure
          label="Need a top-up"
          value={String(low)}
          tone={low > 0 ? "negative" : "positive"}
          sub={low === 0 ? "all funded" : "below their floor"}
        />
      </KpiStrip>
      {funding === undefined || books.length === 0 ? null : (
        <BookAccountant books={books} funding={funding} />
      )}
      <BookExposure
        books={books}
        {...(onStatusChange === undefined ? {} : { onStatusChange })}
      />
      {onFunds === undefined || books.length === 0 ? null : (
        <BookFunds books={books} onSubmit={onFunds} />
      )}
    </div>
  );
}
